// src/pages/admin/events/AdminEventsList.jsx
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../../../config/api";
import { get, del } from "../../../lib/http";
import { AD } from "../../adminUi.js";

const formatDDMonYYYY = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}/${mon}/${yyyy}`;
};
const cn = (...a) => a.filter(Boolean).join(" ");
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function storedRole() {
  try {
    return String(JSON.parse(localStorage.getItem("bb_user") || "null")?.role || "").toLowerCase();
  } catch {
    return "";
  }
}

function eventList(response) {
  if (Array.isArray(response)) return response;
  return response?.events || response?.submissions || response?.data || [];
}

function organizerLabel(event) {
  const owner = event?.createdBy;
  if (!owner) return "";
  if (typeof owner === "string") return owner;
  return owner.name || owner.email || owner.mobile || "";
}

const STATUS_TABS = [
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

function RowActionsMenu({ eventId, eventName, open, onToggle, onClose, onView, onEdit, onDashboard, onDelete }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuW = 176;
    const menuH = 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH && rect.top > menuH;
    setPos({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left: Math.min(Math.max(8, rect.right - menuW), window.innerWidth - menuW - 8),
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    // Defer so the same click that opened the menu doesn't race with listeners
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const runAction = (action) => (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    action();
    onClose();
  };

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: 176,
            transform: pos.openUp ? "translateY(-100%)" : undefined,
          }}
          className="z-[9999] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-900"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-amber-50 dark:text-slate-200 dark:hover:bg-gray-800"
            onMouseDown={runAction(() => onView(eventId))}
          >
            View
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm font-semibold text-amber-900 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-gray-800"
            onMouseDown={runAction(() => onEdit(eventId))}
          >
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm font-semibold text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-gray-800"
            onMouseDown={runAction(() => onDashboard(eventId))}
          >
            Dashboard
          </button>
          <div className="my-1 border-t border-slate-100 dark:border-gray-800" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
            onMouseDown={runAction(() => onDelete(eventId, eventName))}
          >
            Delete
          </button>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative inline-flex">
      <button
        ref={btnRef}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 hover:text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="12" cy="19" r="1.75" />
        </svg>
      </button>
      {menu}
    </div>
  );
}

export default function AdminEventsList() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperadmin = storedRole() === "superadmin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  // UI state — default Approved so organiser events show after approval
  const statusFromUrl = searchParams.get("status");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(
    statusFromUrl || (isSuperadmin ? "approved" : "all")
  );
  const [city, setCity] = useState("all");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("latest");
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const [menuOpenId, setMenuOpenId] = useState(null);

  useEffect(() => {
    if (statusFromUrl && statusFromUrl !== status) setStatus(statusFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFromUrl]);

  const setStatusTab = (value) => {
    setStatus(value);
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("status");
    else next.set("status", value);
    setSearchParams(next, { replace: true });
  };

  /* ---- LOAD role-scoped management events ---- */
  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const endpoint = isSuperadmin ? API.events.allSubmissions : API.events.mySubmissions;
      const list = eventList(await get(endpoint));
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Events fetch error:", e);
      setRows([]);
      setErrorMsg(e?.message || `Failed to load ${isSuperadmin ? "all" : "your"} events.`);
    } finally {
      setLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  /* ---- Derived options ---- */
  const options = useMemo(() => {
    const cities = new Set();
    const cats = new Set();
    rows.forEach((r) => {
      if (r?.venue?.city) cities.add(String(r.venue.city));
      if (r?.category) cats.add(String(r.category));
    });
    return {
      cities: ["all", ...Array.from(cities).sort()],
      categories: ["all", ...Array.from(cats).sort()],
    };
  }, [rows]);

  /* ---- Filters + sort ---- */
  const statusCounts = useMemo(() => {
    const counts = { all: rows.length, approved: 0, pending: 0, rejected: 0 };
    rows.forEach((r) => {
      const key = getStatusBadge(r).key;
      if (key === "approved" || key === "active" || key === "published") counts.approved += 1;
      else if (key === "pending") counts.pending += 1;
      else if (key === "rejected") counts.rejected += 1;
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = [...rows];
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((r) =>
        [r?.name, r?.category, r?.venue?.city, organizerLabel(r)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }
    if (status !== "all") {
      list = list.filter((r) => {
        const key = getStatusBadge(r).key;
        if (status === "approved") {
          return key === "approved" || key === "active" || key === "published";
        }
        return key === status;
      });
    }
    if (city !== "all") {
      list = list.filter(
        (r) => String(r?.venue?.city || "").toLowerCase() === city.toLowerCase()
      );
    }
    if (category !== "all") {
      list = list.filter(
        (r) => String(r?.category || "").toLowerCase() === category.toLowerCase()
      );
    }
    list.sort((a, b) => {
      const aName = (a?.name || "").toLowerCase();
      const bName = (b?.name || "").toLowerCase();
      const aDate = new Date(a?.date || a?.startDate || a?.start || a?.start_time || 0).getTime();
      const bDate = new Date(b?.date || b?.startDate || b?.start || b?.start_time || 0).getTime();
      switch (sort) {
        case "latest": return bDate - aDate;
        case "oldest": return aDate - bDate;
        case "az": return aName.localeCompare(bName);
        case "za": return bName.localeCompare(aName);
        default: return 0;
      }
    });
    return list;
  }, [rows, q, status, city, category, sort]);

  const closeMenu = useCallback(() => setMenuOpenId(null), []);

  const rowMenu = (r) => {
    const id = String(r._id || r.id);
    return (
      <RowActionsMenu
        eventId={id}
        eventName={r?.name || ""}
        open={menuOpenId === id}
        onToggle={() => setMenuOpenId((cur) => (cur === id ? null : id))}
        onClose={closeMenu}
        onView={(eid) => nav(`/admin/events/${eid}`)}
        onEdit={(eid) => nav(`/admin/events/edit/${eid}`)}
        onDashboard={(eid) => nav(`/admin/events/dashboard/${eid}`)}
        onDelete={(eid, name) => setConfirm({ open: true, id: eid, name })}
      />
    );
  };

  /* ---- Delete flow ---- */
  const onDelete = async (id) => {
    setConfirm({ open: false, id: null, name: "" });
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      await del(`${API.events.deleteEvent}/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setRefreshTick((x) => x + 1);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={AD.page}>
      {/* Header */}
      <div className={AD.headerRow}>
        <div>
          <h1 className={AD.title}>
            {isSuperadmin
              ? status === "approved"
                ? "Approved Events"
                : status === "pending"
                  ? "Pending Events"
                  : "All Events"
              : "My Events"}
          </h1>
          <p className={AD.subtitle}>
            {isSuperadmin
              ? "View full details and edit any organiser or admin event."
              : "Create, edit, and manage only the events you own."}
          </p>
        </div>
        <div className={AD.toolbar}>
          <button type="button" onClick={() => setRefreshTick((x) => x + 1)} className={AD.btnSecondary}>
            Refresh
          </button>
          <Link to="/admin/events/new" className={`${AD.btnPrimary} no-underline`}>
            <span className="text-lg leading-none">＋</span>
            New event
          </Link>
        </div>
      </div>

      {isSuperadmin && (
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatusTab(tab.value)}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-bold transition",
                status === tab.value
                  ? "bg-amber-400 text-slate-900 shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:border-amber-300"
              )}
            >
              {tab.label}
              <span className="ml-1.5 opacity-70">({statusCounts[tab.value] ?? 0})</span>
            </button>
          ))}
        </div>
      )}

      {/* error banner */}
      {errorMsg && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm">
          {errorMsg}
        </div>
      )}

      {/* filters */}
      <div className={AD.filterCard}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, city, category, or organiser…"
                className={AD.input}
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-3.5-3.5" />
              </svg>
            </div>
          </div>

          <Select label="City" value={city} onChange={setCity}
            options={options.cities.map((c) => ({ label: c === "all" ? "All" : c, value: c }))} />

          <Select label="Category" value={category} onChange={setCategory}
            options={options.categories.map((c) => ({ label: c === "all" ? "All" : c, value: c }))} />

          <Select label="Sort" value={sort} onChange={setSort}
            options={[
              { label: "Latest", value: "latest" },
              { label: "Oldest", value: "oldest" },
              { label: "A → Z", value: "az" },
              { label: "Z → A", value: "za" },
            ]} />
        </div>
      </div>

      {/* ===== MOBILE LIST (cards) ===== */}
      <div className="md:hidden space-y-3">
        {loading &&
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`sk-m-${i}`} className="rounded-2xl border bg-white dark:bg-gray-900 p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="h-16 w-20 rounded-lg bg-slate-200 dark:bg-gray-800" />
                <div className="flex-1">
                  <div className="h-4 w-40 rounded bg-slate-200 dark:bg-gray-800" />
                  <div className="mt-2 h-3 w-24 rounded bg-slate-200 dark:bg-gray-800" />
                </div>
              </div>
            </div>
          ))}

        {!loading && filteredRows.map((r) => {
          const start = r?.date || r?.startDate || r?.start_time || r?.start;
          const end = r?.endDate || r?.end_time || r?.end;
          const badge = getStatusBadge(r);
          const thumb = r?.imageUrl || r?.banner || r?.cover;

          return (
            <div key={`m-${r._id || r.id}`} className="rounded-2xl border bg-white dark:bg-gray-900 shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-16 w-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-800 border">
                  {thumb ? (
                    <img src={thumb} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-slate-400">🎫</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 dark:text-white truncate">
                    {r?.name || "—"}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {r?.venue?.name ? `${r.venue.name} • ` : ""}
                    {r?.venue?.city || "—"}
                  </div>
                </div>
                <span className={cn(
                  "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
                  badge.cls
                )}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                  {badge.label}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300">
                <div><span className="font-semibold">Category:</span> {r?.category || "—"}</div>
                <div><span className="font-semibold">Start:</span> {formatDDMonYYYY(start)}</div>
                <div><span className="font-semibold">End:</span> {formatDDMonYYYY(end)}</div>
                {r?.totals?.revenue ? (
                  <div><span className="font-semibold">Revenue:</span> {inr(r.totals.revenue)}</div>
                ) : null}
              </div>

              <div className="mt-3 flex items-center justify-end">
                {rowMenu(r)}
              </div>
            </div>
          );
        })}

        {!loading && filteredRows.length === 0 && (
          <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-slate-600 dark:text-slate-400">
            No events match your filters.
          </div>
        )}

        {/* mobile count */}
        <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-center text-xs text-slate-500 mt-2">
          Showing <span className="font-semibold">{filteredRows.length}</span> of{" "}
          <span className="font-semibold">{rows.length}</span> events
        </div>
      </div>

      {/* ===== DESKTOP TABLE ===== */}
      <div className={`hidden md:block ${AD.tableShell}`}>
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className={AD.thead}>
              <tr>
                <Th>Event</Th>
                <Th>Organiser</Th>
                <Th>Category</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="animate-pulse">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 rounded-lg bg-slate-200 dark:bg-gray-800" />
                        <div>
                          <div className="h-4 w-40 rounded bg-slate-200 dark:bg-gray-800" />
                          <div className="mt-2 h-3 w-24 rounded bg-slate-200 dark:bg-gray-800" />
                        </div>
                      </div>
                    </Td>
                    <Td><div className="h-4 w-16 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-4 w-20 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-4 w-20 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td className="text-right"><div className="h-8 w-24 rounded-full bg-slate-200 dark:bg-gray-800 ml-auto" /></Td>
                  </tr>
                ))}

              {!loading && filteredRows.map((r) => {
                const start = r?.date || r?.startDate || r?.start_time || r?.start;
                const end = r?.endDate || r?.end_time || r?.end;
                const badge = getStatusBadge(r);
                const thumb = r?.imageUrl || r?.banner || r?.cover;

                return (
                  <tr key={r._id || r.id} className="hover:bg-amber-50/40 dark:hover:bg-gray-800/40 transition-colors">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-800 border">
                          {thumb ? (
                            <img src={thumb} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full grid place-items-center text-slate-400">🎫</div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white truncate">
                            {r?.name || "—"}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {r?.venue?.name ? `${r.venue.name} • ` : ""}
                            {r?.venue?.city || "—"}
                          </div>
                        </div>
                      </div>
                    </Td>
                    <Td className="max-w-[10rem] truncate whitespace-nowrap text-slate-600">
                      {organizerLabel(r) || "—"}
                    </Td>
                    <Td className="whitespace-nowrap">{r?.category || "—"}</Td>
                    <Td className="whitespace-nowrap">{formatDDMonYYYY(start)}</Td>
                    <Td className="whitespace-nowrap">{formatDDMonYYYY(end)}</Td>
                    <Td>
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                        badge.cls
                      )}>
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {badge.label}
                      </span>
                    </Td>
                    <Td className="text-right">
                      {rowMenu(r)}
                    </Td>
                  </tr>
                );
              })}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <div className="text-5xl mb-2">🔍</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      No events match your filters. Try adjusting search or create a new one.
                    </div>
                    <Link
                      to="/admin/events/new"
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                    >
                      Create Event
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 text-xs text-slate-500 border-t dark:border-gray-800">
          Showing <span className="font-semibold">{filteredRows.length}</span> of{" "}
          <span className="font-semibold">{rows.length}</span> events
        </div>
      </div>

      {/* Confirm modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm({ open: false, id: null, name: "" })} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl p-6">
            <div className="text-lg font-bold text-slate-900 dark:text-white">Delete Event</div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Are you sure you want to delete <span className="font-semibold">{confirm.name}</span>? This action cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirm({ open: false, id: null, name: "" })}
                className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-amber-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => onDelete(confirm.id)}
                className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-semibold hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* tiny bits */
function Th({ children, className = "" }) {
  return <th className={cn("py-3 px-4 font-semibold", className)}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={cn("py-4 px-4 align-middle", className)}>{children}</td>;
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} className={AD.select}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}

function getStatusBadge(r) {
  const approval = String(r?.approvalStatus || "").toLowerCase();
  if (approval === "pending") return { key: "pending", cls: "bg-amber-100 text-amber-800", label: "Pending Approval" };
  if (approval === "approved") return { key: "approved", cls: "bg-emerald-100 text-emerald-700", label: "Approved" };
  if (approval === "rejected") return { key: "rejected", cls: "bg-rose-100 text-rose-700", label: "Rejected" };
  if (r?.isActived === true) return { key: "active", cls: "bg-emerald-100 text-emerald-700", label: "Active" };
  if (r?.isActived === false) return { key: "inactive", cls: "bg-rose-100 text-rose-700", label: "Inactive" };
  if (r?.status === "published") return { key: "published", cls: "bg-emerald-100 text-emerald-700", label: "Published" };
  if (r?.status === "draft") return { key: "draft", cls: "bg-slate-200 text-slate-800", label: "Draft" };
  // Legacy / admin-created events without approvalStatus → treat as approved
  if (!approval) return { key: "approved", cls: "bg-emerald-100 text-emerald-700", label: "Approved" };
  return { key: "unknown", cls: "bg-amber-100 text-amber-800", label: r?.status || "Unknown" };
}

